import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, MapPin, Clock, Instagram } from 'lucide-react';
import UfokkoLogo from './UfokkoLogo';
import { CONTACT_EMAIL, INSTAGRAM_HANDLE, INSTAGRAM_URL } from '../constants/contact';

/**
 * Layout compartilhado para páginas públicas: header (logo + nav) e footer iguais à landing.
 * Uso: <PublicLayout><Login /></PublicLayout> ou wrap na rota.
 */
const PublicLayout = ({ children, className = '' }) => {
  return (
    <div className={`min-h-screen flex flex-col bg-gradient-to-br from-gray-50 via-blue-50 to-blue-100 dark:from-[#0F1629] dark:via-[#1A2332] dark:to-[#0F1629] ${className}`}>
      {/* Header - mesma nav da landing */}
      <header className="relative z-10 border-b border-gray-200/50 dark:border-gray-700/50 bg-white/80 dark:bg-[#0F1629]/80 backdrop-blur-sm">
        <nav className="container mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity" aria-label="Ufokko - Início">
              <UfokkoLogo size="lg" color="gradient" />
            </Link>
            <div className="hidden md:flex items-center gap-6">
              <Link to="/#como-funciona" className="text-gray-700 dark:text-gray-200 hover:text-primary font-semibold transition-colors">
                Como Funciona
              </Link>
              <Link to="/#categorias" className="text-gray-700 dark:text-gray-200 hover:text-primary font-semibold transition-colors">
                Categorias
              </Link>
              <Link to="/para-videomakers" className="text-gray-700 dark:text-gray-200 hover:text-primary font-semibold transition-colors">
                Sou videomaker
              </Link>
              <Link to="/ajuda" className="text-gray-700 dark:text-gray-200 hover:text-primary font-semibold transition-colors">
                Ajuda
              </Link>
              <Link to="/login" className="px-5 py-2.5 text-gray-700 dark:text-gray-200 hover:text-primary font-semibold transition-colors">
                Entrar
              </Link>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link
                  to="/cadastro"
                  className="inline-block px-6 py-2.5 bg-gradient-to-r from-[#0E76FF] to-[#2563EB] text-white rounded-full font-semibold shadow-lg hover:shadow-xl transition-shadow"
                >
                  Cadastrar
                </Link>
              </motion.div>
            </div>
          </div>
        </nav>
      </header>

      <main className="flex-1">
        {children}
      </main>

      {/* Footer - mesmo da landing */}
      <footer className="py-12 bg-gray-900 dark:bg-black text-white mt-auto">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <UfokkoLogo size="md" color="default" />
              </div>
              <p className="text-gray-400 mb-4">
                Conectando clientes a videomakers profissionais em todo o Brasil
              </p>
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-gray-300 hover:text-white transition-colors"
                aria-label={`Instagram @${INSTAGRAM_HANDLE}`}
              >
                <Instagram className="w-5 h-5 shrink-0" aria-hidden />
                <span>Siga @{INSTAGRAM_HANDLE}</span>
              </a>
            </div>
            <div>
              <h3 className="font-bold mb-4">Acesso Rápido</h3>
              <ul className="space-y-2 text-gray-400">
                <li>
                  <Link to="/login" className="hover:text-white transition-colors">Login</Link>
                </li>
                <li>
                  <Link to="/cadastro" className="hover:text-white transition-colors">Cadastre-se</Link>
                </li>
                <li>
                  <Link to="/ajuda" className="hover:text-white transition-colors">Central de Ajuda</Link>
                </li>
                <li>
                  <Link to="/" className="hover:text-white transition-colors">Voltar ao início</Link>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold mb-4">Informações</h3>
              <ul className="space-y-2 text-gray-400">
                <li>
                  <Link to="/termos-de-uso" className="hover:text-white transition-colors">Termos de Uso</Link>
                </li>
                <li>
                  <Link to="/politica-de-privacidade" className="hover:text-white transition-colors">Política de Privacidade</Link>
                </li>
                <li>
                  <Link to="/politica-cancelamento-reembolso" className="hover:text-white transition-colors">Política de Cancelamento e Reembolso</Link>
                </li>
                <li>
                  <Link to="/politica-repasses-videomaker" className="hover:text-white transition-colors">Política de Repasses (Videomakers)</Link>
                </li>
                <li>
                  <Link to="/ajuda" className="hover:text-white transition-colors">Central de Ajuda</Link>
                </li>
                <li>
                  <Link to="/#como-funciona" className="hover:text-white transition-colors">Como Funciona</Link>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold mb-4">Contato</h3>
              <ul className="space-y-2 text-gray-400">
                <li className="flex items-center gap-2">
                  <Mail className="w-4 h-4 shrink-0" aria-hidden />
                  <a href={`mailto:${CONTACT_EMAIL}`} className="hover:text-white transition-colors">
                    {CONTACT_EMAIL}
                  </a>
                </li>
                <li className="flex items-center gap-2">
                  <Instagram className="w-4 h-4 shrink-0" aria-hidden />
                  <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                    @{INSTAGRAM_HANDLE}
                  </a>
                </li>
                <li className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 shrink-0" aria-hidden />
                  <span>Rio de Janeiro, RJ</span>
                </li>
                <li className="flex items-center gap-2">
                  <Clock className="w-4 h-4 shrink-0" aria-hidden />
                  <span>Seg - Sex: 9h - 18h</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-gray-400">
            <p>&copy; 2026 Ufokko. Todos os direitos reservados.</p>
            <p className="text-sm mt-2">
              Ufokko - Conectando clientes a videomakers profissionais em tempo real
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PublicLayout;
